import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import { MICROSOFT_WEB_REDIRECT_URI } from '@/lib/email/config';

export function OutlookConnected() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const hasHandled = useRef(false);
  const [status, setStatus] = useState('Connecting your Outlook account...');

  useEffect(() => {
    const run = async () => {
      if (hasHandled.current) return;
      hasHandled.current = true;

      try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const returnedState = params.get('state');
        const oauthError = params.get('error');
        const oauthErrorDesc = params.get('error_description');

        if (oauthError) {
          throw new Error(oauthErrorDesc || oauthError);
        }

        const expectedState = sessionStorage.getItem('outlook_oauth_state');
        const codeVerifier = sessionStorage.getItem('outlook_oauth_verifier');

        // CSRF: abort before any network call on a state mismatch.
        if (!code || !returnedState || returnedState !== expectedState) {
          throw new Error('Authentication response could not be verified. Please try connecting again.');
        }
        if (!codeVerifier) {
          throw new Error('Connection session expired. Please try connecting again.');
        }

        const workspaceId = localStorage.getItem('connecting_workspace_id');
        if (!workspaceId) throw new Error('No workspace selected. Please try connecting again.');

        const connectingProvider = localStorage.getItem('connecting_provider');
        const isCalendar = connectingProvider === 'outlook_calendar';

        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('Not authenticated. Please sign in and try again.');

        setStatus('Finishing setup...');
        const { data, error } = await supabase.functions.invoke('outlook-oauth-exchange', {
          body: {
            code,
            code_verifier: codeVerifier,
            workspace_id: workspaceId,
            provider: connectingProvider || 'outlook',
            redirect_uri: MICROSOFT_WEB_REDIRECT_URI,
          },
        });

        // supabase.functions.invoke surfaces non-2xx as `error` (a
        // FunctionsHttpError); in supabase-js v2 the parsed JSON body of a 4xx
        // is on `error.context` (a Response-like object), NOT reliably on
        // `data`. Try to read the structured `{ error }` from the body for a
        // useful message, then fall back. (Executor: confirm the exact shape
        // empirically in Task 1.12 — log `error` + `data` from a forced 400.)
        if (error) {
          let serverMsg: string | undefined;
          try {
            const ctxBody = await (error as any)?.context?.json?.();
            serverMsg = ctxBody?.error;
          } catch { /* body already consumed or not JSON */ }
          throw new Error(serverMsg || data?.error || error.message || 'Failed to connect Outlook.');
        }
        if (data?.error) throw new Error(data.error);

        // Clean up flow state.
        sessionStorage.removeItem('outlook_oauth_state');
        sessionStorage.removeItem('outlook_oauth_verifier');
        localStorage.removeItem('connecting_workspace_id');
        localStorage.removeItem('connecting_provider');

        toast({ description: 'Outlook account connected successfully' });
        navigate(isCalendar ? '/settings/calendar' : '/settings/senders');
      } catch (err) {
        console.error('[OutlookConnected] Error:', err);
        captureError(err as Error, { feature: 'outlook-oauth-connected' });
        setStatus('Error connecting account');
        toast({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to connect Outlook account',
          variant: 'destructive',
        });
        setTimeout(() => navigate('/settings/senders'), 2500);
      }
    };
    run();
  }, [navigate, toast]);

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="text-center space-y-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
        <p className="text-muted-foreground">{status}</p>
      </div>
    </div>
  );
}
